import { SchemaIO } from '@frontend/schema';
import { useUnifiedThreadUpdater } from '../../v4/query-updaters';
import { OptimisticUpdateStatusDetails, ServiceMutations } from '../types';
import { SMSMutationContext } from '../types';
import { UseSMSDataV3MutationEndpointArgs, useSMSDataV3Mutation } from './use-sms-data-v3-mutation';

type MutationContext<C = unknown> = {
  mutationContext: SMSMutationContext & { didOptimisticUpdate: boolean };
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `DeleteSMS` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `true`.
 */
export const useDeleteSMSMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends {
    threadId: string;
  } = {
    threadId: string;
  },
  RequestOverride extends SchemaIO<ServiceMutations['DeleteSMS']>['input'] = SchemaIO<
    ServiceMutations['DeleteSMS']
  >['input']
>({
  options,
  optimisticUpdate = true,
  ...args
}: UseSMSDataV3MutationEndpointArgs<'DeleteSMS', E, C | undefined, OtherOptions, RequestOverride> & {
  optimisticUpdate?: boolean;
} = {}) => {
  const unifiedThreadUpdater = useUnifiedThreadUpdater({ httpOptions: args.httpOptions });

  return useSMSDataV3Mutation<'DeleteSMS', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'DeleteSMS',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        const mutationContext: MutationContext['mutationContext'] = {
          didOptimisticUpdate: false,
        };

        if (optimisticUpdate) {
          if (request._otherOptions?.threadId) {
            const result = await unifiedThreadUpdater.deleteThreadItem({
              matchValues: {
                threadItemId: request.smsId,
                threadId: request._otherOptions.threadId,
                locationId: request.locationId,
              },
              statusDetails: OptimisticUpdateStatusDetails.DELETING,
              isOptimistic: true,
            });

            if (result.revertCacheUpdate) {
              mutationContext.didOptimisticUpdate = true;
              mutationContext.revertOptimisticUpdate = result.revertCacheUpdate;
            }
          }
        }
        return {
          mutationContext,
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: async (response, request, context) => {
        if (request._otherOptions?.threadId) {
          await unifiedThreadUpdater.deleteThreadItem({
            matchValues: {
              threadItemId: request.smsId,
              threadId: request._otherOptions.threadId,
              locationId: request.locationId,
            },
            statusDetails: '',
            isOptimistic: false,
          });
        }

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        if (context?.mutationContext.didOptimisticUpdate) {
          context?.mutationContext.revertOptimisticUpdate?.();
        }

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
